import type { LastAddedItemMethod } from '@ori-events/basket-updated';
import * as BasketUpdatedEvent from '@ori-events/basket-updated';
import * as ToggleMenuFlyoutEvent from '@ori-events/toggle-menu-flyout';
import { type FC } from 'react';
import { createRef, useEffect, useState } from 'react';
import { Transition, TransitionGroup } from 'react-transition-group';
import { v4 } from 'uuid';

import { bubbleDuration } from '../../constants';
import { isMinibagVisible } from '../../utils';
import { Bubble, type BubbleInstanceProps } from '../Bubble';
import { AnimationWrapper, BasketBubbleWrapper } from './styles';

export const BubbleGroup: FC = () => {
  const [bubbles, setBubbles] = useState<BubbleInstanceProps[]>([]);

  const removeBasketBubble = (bubbleId: string) => {
    setBubbles((currentBubbles) => currentBubbles.filter((currentBubble) => currentBubble.id !== bubbleId));
  };

  const onToggleMenuFlyout = ((event: ToggleMenuFlyoutEvent.Event) => {
    const { menuType } = event.detail;
    if (menuType === ToggleMenuFlyoutEvent.MenuTypes.Minibag && isMinibagVisible()) {
      // hide all bubbles when minibag is being opened
      setBubbles([]);
    }
  }) as EventListener;

  const onBasketUpdate = ((event: BasketUpdatedEvent.Event) => {
    // don't show bubble if not add-to-basket update or if minibag is visible
    if (!event.detail.lastAddedItem || isMinibagVisible()) {
      return;
    }

    const { itemCode, method } = event.detail.lastAddedItem;
    const { shoppingBagItems, offers } = event.detail.shoppingBag;
    const offersItems = offers.reduce(
      (acc: BasketUpdatedEvent.ShoppingBagItem[], offer: BasketUpdatedEvent.ShoppingBagOffer) => [
        ...acc,
        ...offer.items,
      ],
      [],
    );

    // proper typing for method -- TODO
    const methodType = method as unknown as LastAddedItemMethod;

    const item =
      methodType === BasketUpdatedEvent.LastAddedItemMethod.BUY_FOR_MONEY
        ? shoppingBagItems.find((product) => product.itemCode === itemCode)
        : offersItems.find((product) => product.itemCode === itemCode);

    if (!item) {
      return;
    }

    const bubble: BubbleInstanceProps = {
      id: v4(),
      code: item.itemCode,
      name: item.itemDescription,
      price: item.totalPrice,
      quantity: item.requestedQuantity,
      image: item.itemImageSrc,
      points: item.totalRedeemedPoints,
      methodType,
      nodeRef: createRef(),
    };
    setBubbles((currentBubbles) => [...currentBubbles, bubble]);
    setTimeout(() => removeBasketBubble(bubble.id), bubbleDuration);
  }) as EventListener;

  useEffect(() => {
    window.addEventListener(BasketUpdatedEvent.Name, onBasketUpdate);
    window.addEventListener(ToggleMenuFlyoutEvent.Name, onToggleMenuFlyout);

    return () => {
      window.removeEventListener(BasketUpdatedEvent.Name, onBasketUpdate);
      window.removeEventListener(ToggleMenuFlyoutEvent.Name, onToggleMenuFlyout);
    };
  });

  const timeoutSettings = {
    appear: 0,
    enter: 0,
    exit: 1250,
  };

  return (
    <BasketBubbleWrapper>
      <TransitionGroup>
        {bubbles.map(({ id, nodeRef, ...bubble }) => (
          <Transition
            key={id}
            nodeRef={nodeRef}
            timeout={timeoutSettings}
          >
            {(state) => {
              const bubbleOwnerState = { state };

              return (
                <AnimationWrapper
                  ref={nodeRef}
                  key={id}
                  ownerState={bubbleOwnerState}
                >
                  <Bubble
                    key={id}
                    {...bubble}
                  />
                </AnimationWrapper>
              );
            }}
          </Transition>
        ))}
      </TransitionGroup>
    </BasketBubbleWrapper>
  );
};
